import { useState } from "react";

import { toFormData } from "@king-ict/eupisi-ui/utils";
import { useDispatch } from "react-redux";

import {
  createApplicantParentDocument,
  createFromExistingApplicantParentDocument,
  deleteApplicantParentDocument,
  getApplicantParentDocument,
} from "../../../api/kindergartenApi";
import ApplicantParent from "../../../models/Person/ApplicantParent";
import DocumentMethodsReturnType, {
  DocumentDownloadRemoveMethodReturnType,
} from "../../../models/Request/DocumentMethodsReturnType";
import Request from "../../../models/Request/Request";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import UploadDocument from "../../../models/Request/UploadDocument";
import { updateRequestApplicantDocuments } from "../../../slices/requestSlice";

interface Props {
  setApplicant: (prev: (prevData: ApplicantParent) => ApplicantParent) => void;
  requestState: Request;
}
export function useApplicantParentDocuments(): DocumentDownloadRemoveMethodReturnType;
export function useApplicantParentDocuments(
  props: Props
): DocumentMethodsReturnType;
export function useApplicantParentDocuments(props?: Props) {
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const handleDownloadDocument = (document: RequestDocumentData) => {
    if (document.fileID) {
      getApplicantParentDocument(document.fileID);
    }
  };
  const dispatch = useDispatch();

  const handleRemoveDocuments = (documents: RequestDocumentData[]) => {
    let ids = new Array<number>();
    documents.forEach((document) => {
      if (document.id) {
        ids.push(document.id);
      }
    });

    if (ids.length > 0) {
      deleteApplicantParentDocument(ids);
    }
  };

  if (props) {
    const { setApplicant, requestState } = props;

    const handleUploadDocuments = async (files: File[]) => {
      var documents = await createApplicantParentDocument(
        toFormData<UploadDocument>({
          requestUID: requestState.uId,
          applicantParentOib: requestState.applicantParent.oib,
          childOib: requestState.child.oib,
          files: files,
        })
      );
      return documents;
    };

    const handleUploadExistingDocument = async (
      document: RequestDocumentData
    ) => {
      var docResult = await createFromExistingApplicantParentDocument(
        document.fileID!,
        document.origin
      );
      return docResult;
    };

    const handleConfirmDocumentsModal = (documents: RequestDocumentData[]) => {
      setApplicant((prev) => {
        const newDocuments = prev.documents
          ? [...prev.documents, ...documents]
          : [...documents];

        return { ...prev, documents: newDocuments };
      });

      setIsDocumentsModalOpen((isDocumentsModalOpen) => !isDocumentsModalOpen);
    };

    const handleCloseDocumentsModal = (documents: RequestDocumentData[]) => {
      handleRemoveDocuments(documents);
      setIsDocumentsModalOpen((isDocumentsModalOpen) => !isDocumentsModalOpen);
    };

    const handleToggleDocumentsModal = () => {
      setIsDocumentsModalOpen((isDocumentsModalOpen) => !isDocumentsModalOpen);
    };

    const handleRemoveDocument = (document: RequestDocumentData) => {
      if (document.id) {
        const ids = [document.id];
        deleteApplicantParentDocument(ids);
      }
    };

    const handleRemoveDocumentFromList = (document: RequestDocumentData) => {
      setApplicant((prev) => {
        if (prev.documents) {
          const newDocuments = prev.documents.filter(
            (d) => d.id !== document.id
          );
          dispatch(updateRequestApplicantDocuments(newDocuments));
          return { ...prev, documents: newDocuments };
        }
        return prev;
      });
      handleRemoveDocument(document);
    };

    return {
      isDocumentsModalOpen,
      handleUploadDocuments,
      handleUploadExistingDocument,
      handleConfirmDocumentsModal,
      handleCloseDocumentsModal,
      handleToggleDocumentsModal,
      handleDownloadDocument,
      handleRemoveDocumentFromList,
      handleRemoveDocument,
      handleRemoveDocuments,
    };
  } else {
    return {
      handleDownloadDocument,
      handleRemoveDocuments,
    };
  }
}
